import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { useForesight } from "@frontend/hooks/useForesight";
import { useLingui } from "@lingui/react/macro";

import type { PaginationNavigationButtonProps } from "./Pagination.types";
import { NAVIGATION_ICONS, PREFETCH_REACTIVATE_MS } from "./constants";

export function PaginationNavigationButton({
  direction,
  variant,
  size,
  compact,
  disabled,
  onClick,
  onPrefetch,
}: PaginationNavigationButtonProps): JSX.Element {
  const { t } = useLingui();

  const { elementRef, onFocus } = useForesight<HTMLButtonElement>({
    name: `Pagination ${direction}`,
    reactivateAfter: PREFETCH_REACTIVATE_MS,
    callback: onPrefetch && !disabled ? onPrefetch : undefined,
  });

  const label = direction === "previous" ? t`Previous` : t`Next`;

  const title = direction === "previous" ? t`Previous page` : t`Next page`;

  const iconElement = <Icon name={NAVIGATION_ICONS[direction]} size={size} />;

  if (variant !== "minimal" || compact) {
    return (
      <Button
        ref={elementRef}
        onFocus={onFocus}
        size={size}
        variant="ghost"
        disabled={disabled}
        onClick={onClick}
        aria-label={title}
      >
        {iconElement}
      </Button>
    );
  }

  const labelElement = <span>{label}</span>;

  return (
    <Button
      ref={elementRef}
      onFocus={onFocus}
      size={size}
      variant="neutral"
      disabled={disabled}
      onClick={onClick}
      aria-label={title}
    >
      {direction === "previous" ? iconElement : labelElement}
      {direction === "previous" ? labelElement : iconElement}
    </Button>
  );
}
